import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';

/**
 * Controlled New/Edit Asset form modal. All state and mutations live in the
 * parent (Assets.jsx) and are passed in via props; this component only renders
 * the form and forwards events.
 *
 * Constants (assetTypeDefaults, assetFrequencyMap, electricalAssetTypes,
 * fitmentsAssetTypes, conditionRatings) are passed as props so the parent
 * can reuse them in openEdit / resetForm without duplication.
 */
export default function AssetFormModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  isEditing,
  isSubmitting,
  sites,
  electricalAssetTypes,
  fitmentsAssetTypes,
  assetFrequencyMap,
  assetTypeDefaults,
  conditionRatings,
}) {
  const update = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const isBoardOrRcd =
    formData.assetType === 'rcd' || formData.assetType === 'distribution_board';

  const handleElementTypeChange = (e) => {
    const et = e.target.value;
    const defaultType = et === 'fitments' ? 'workshop_equipment' : 'exit_light';
    const defaults = assetTypeDefaults[defaultType] || {};
    setFormData({
      ...formData,
      elementType: et,
      assetType: defaultType,
      testFrequencyMonths: assetFrequencyMap[defaultType] || 12,
      element: defaults.element || '',
      component: defaults.component || '',
      itemType: defaults.itemType || '',
    });
  };

  const handleAssetTypeChange = (e) => {
    const type = e.target.value;
    const defaults = assetTypeDefaults[type] || {};
    setFormData({
      ...formData,
      assetType: type,
      testFrequencyMonths: assetFrequencyMap[type] || 12,
      element: defaults.element || '',
      component: defaults.component || '',
      itemType: defaults.itemType || '',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Asset' : 'New Asset'}>
      <form onSubmit={onSubmit}>
        {!isEditing && (
          <Select
            id="asset-form-site"
            label="Site *"
            value={formData.siteId}
            onChange={update('siteId')}
            required
          >
            <option value="">Select site</option>
            {sites?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.display_name ?? s.name ?? s.site_code ?? 'Unnamed site'}
              </option>
            ))}
          </Select>
        )}
        <Select
          id="asset-form-element-type"
          label="Element Type *"
          value={formData.elementType}
          onChange={handleElementTypeChange}
          required
        >
          <option value="electrical">Electrical</option>
          <option value="fitments">Fitments</option>
        </Select>
        <Select
          id="asset-form-asset-type"
          label="Asset Type *"
          value={formData.assetType}
          onChange={handleAssetTypeChange}
          required
        >
          {(formData.elementType === 'fitments' ? fitmentsAssetTypes : electricalAssetTypes).map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <div className="form-row">
          <Input
            id="asset-form-building"
            label="Building *"
            value={formData.building}
            onChange={update('building')}
            required
            placeholder="e.g. B00, Block A"
          />
          <Input
            id="asset-form-room"
            label="Room *"
            value={formData.room}
            onChange={update('room')}
            required
            placeholder="e.g. R0001, HR0002"
          />
        </div>
        {isBoardOrRcd && (
          <div className="form-row">
            <Input
              id="asset-form-db-number"
              label="DB Number"
              value={formData.dbNumber}
              onChange={update('dbNumber')}
              placeholder="e.g. DB13A"
            />
            <Input
              id="asset-form-circuit-breaker-number"
              label="Circuit Breaker Number"
              value={formData.circuitBreakerNumber}
              onChange={update('circuitBreakerNumber')}
              placeholder="e.g. CB01 (leave blank for the board itself)"
            />
          </div>
        )}
        <div className="form-row">
          {!isBoardOrRcd && (
            <Input
              id="asset-form-equipment-code"
              label="Equipment Code"
              value={formData.equipmentCode}
              onChange={update('equipmentCode')}
              placeholder="Optional display label (e.g. DB13A - CB01)"
            />
          )}
          <Input
            id="asset-form-manufacturer"
            label="Manufacturer"
            value={formData.manufacturer}
            onChange={update('manufacturer')}
          />
          <Input
            id="asset-form-model"
            label="Model"
            value={formData.model}
            onChange={update('model')}
          />
        </div>
        <div className="form-row">
          <Input
            id="asset-form-serial"
            label="Serial Number"
            value={formData.serialNumber}
            onChange={update('serialNumber')}
          />
          <Input
            id="asset-form-installation-date"
            label="Installation Date"
            type="date"
            value={formData.installationDate}
            onChange={update('installationDate')}
          />
          <Input
            id="asset-form-test-frequency"
            label="Test Frequency (months)"
            type="number"
            value={formData.testFrequencyMonths}
            readOnly
            inputClassName="input-readonly"
            title="Auto-set by asset type (Australian Standards)"
          />
        </div>
        <div className="form-row">
          <Select
            id="asset-form-condition"
            label="Condition"
            value={formData.conditionRating}
            onChange={update('conditionRating')}
          >
            <option value="">— Select condition —</option>
            {conditionRatings.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>
          <Input
            id="asset-form-capacity"
            label="Capacity"
            value={formData.capacity}
            onChange={update('capacity')}
            placeholder="e.g. 16A, 50A, 2.4kW"
          />
        </div>
        <div className="form-row">
          <Input
            id="asset-form-element"
            label="Element"
            value={formData.element}
            readOnly
            inputClassName="input-readonly"
            title="Auto-set by asset type"
          />
          <Input
            id="asset-form-component"
            label="Component"
            value={formData.component}
            readOnly
            inputClassName="input-readonly"
            title="Auto-set by asset type"
          />
        </div>
        <Input
          id="asset-form-item-type"
          label="Item / Type"
          value={formData.itemType}
          onChange={formData.elementType === 'fitments'
            ? update('itemType')
            : undefined}
          readOnly={formData.elementType !== 'fitments'}
          inputClassName={formData.elementType !== 'fitments' ? 'input-readonly' : ''}
          placeholder={formData.elementType === 'fitments' ? 'e.g. Bandsaw, Air Compressor, Pool Pump' : undefined}
          title={formData.elementType !== 'fitments' ? 'Auto-set by asset type' : undefined}
        />
        <Textarea
          id="asset-form-notes"
          label="Notes"
          value={formData.notes}
          onChange={update('notes')}
          rows={3}
        />
        <div className="form-actions">
          <Button type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? (isEditing ? 'Updating…' : 'Creating…') : (isEditing ? 'Update' : 'Create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
